/* eslint-disable */
import { Observable, ReplaySubject, concatMap, delay, first, of } from 'rxjs';
import { LoadingService } from 'src/app/core/services/loading.service';
import { Cache, ImageBitmapLoader, LinearFilter, LinearMipMapLinearFilter, Source, Texture } from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

export interface LoadingRequestObject {
  url: string;
  priority: number;
}

interface AssetLoad {
  url: string;
  priority: number;
  subject: ReplaySubject<any>;
  type: 'texture' | 'gltf';
  usePooling: boolean;
  request?: any;
}

export class AssetLoader {
  private imageLoader = new ImageBitmapLoader();
  private gltfLoader = new GLTFLoader();
  private loadingQueue: AssetLoad[] = [];
  private dracoLoader = new DRACOLoader();
  private currentLoading: AssetLoad[] = [];

  private sharedSubject = new ReplaySubject<GLTF | ImageBitmap>(1); // this is the shared subject
  private sharedObservable = this.sharedSubject.asObservable();
  private timerSubject = new ReplaySubject<any>(1);
  private timerObservable = this.timerSubject.pipe(concatMap((value) => of(value).pipe(delay(1000))));

  private loadingService: LoadingService;

  private _texturePool: Map<string, Observable<Texture | GLTF>> = new Map();
  private _imagePool: Map<string, Observable<Source>> = new Map();

  constructor(loadingService: LoadingService) {
    this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    this.gltfLoader.setDRACOLoader(this.dracoLoader);

    Cache.enabled = true;

    this.timerObservable.subscribe((res) => {
      this.sharedSubject.next(res);
    });

    this.loadingService = loadingService;
  }

  //Če damo več loadingRequestObjectov v array, bodo vezani na isti replaySubject/observable
  loadTexture(loadingRequestObjects: LoadingRequestObject[], usePooling = true): Observable<Texture> {
    const subject = new ReplaySubject<any>(1);
    for (const loadingRequestObject of loadingRequestObjects) {
      this.loadAsset(loadingRequestObject.url, loadingRequestObject.priority, 'texture', subject, usePooling);
    }
    return subject.asObservable();
  }

  loadGLTF(loadingRequestObjects: LoadingRequestObject[]): Observable<any> {
    const subject = new ReplaySubject<any>(1);
    for (const loadingRequestObject of loadingRequestObjects) {
      this.loadAsset(loadingRequestObject.url, loadingRequestObject.priority, 'gltf', subject, false);
    }
    return subject.asObservable();
  }

  private checkCache(
    url: string,
    priority: number,
    type: 'texture' | 'gltf',
    subject: ReplaySubject<any>,
    usePooling = true,
  ): Observable<any> | null {
    const cachedAsset = Cache.get(url);

    if (cachedAsset) {
      this.loadAssetAsync({ url, priority, subject, type, usePooling: usePooling });
      return subject.asObservable();
    }

    return null;
  }

  private loadAsset(
    url: string,
    priority: number,
    type: 'texture' | 'gltf',
    subject: ReplaySubject<any>,
    usePooling = true,
  ): Observable<any> {
    let checked = this.checkCache(url, priority, type, subject, usePooling);
    if (checked) return checked;
    this.loadingQueue.push({ url, priority, subject, type, usePooling });
    this.loadingQueue.sort((a, b) => a.priority - b.priority);

    if (this.loadingQueue[0].priority >= priority || this.currentLoading[0].priority >= priority) {
      this.loadNextAssets();
    }

    return subject.asObservable();
  }

  private loadNextAssets() {
    //Če ni nič za naložit
    if (this.loadingQueue.length === 0) {
      return;
    }

    let currentPriority = this.getLowestPrirority();

    const assetsToLoad = this.loadingQueue.filter((asset) => asset.priority === currentPriority);
    this.loadingQueue = this.loadingQueue.filter((asset) => asset.priority > currentPriority);

    for (const asset of assetsToLoad) {
      this.loadAssetAsync(asset);
    }
    // Re-sort the queue after adding aborted assets back
    this.loadingQueue.sort((a, b) => a.priority + b.priority);
  }

  private loadAssetAsync(asset: AssetLoad) {
    //Poolamo texture, torej pouporabimo po potrebi, ob dolocenih pogojih ne smemo pouporabut
    //če dovolimo pooling tekstur, vzame kr ustvarjeno teksturo
    const pooledAsset = this._texturePool.get(asset.url);
    if (pooledAsset && asset.usePooling) {
      pooledAsset.pipe(first()).subscribe((pooled) => {
        asset.subject.next(pooled);
        asset.subject.complete();
      });
      return;
    }
    this._texturePool.set(asset.url, asset.subject.asObservable());

    //če ne dovolimo, preverimo najprej če že imamo poolan source in ustvarimo iz sourca
    const pooledImage = this._imagePool.get(asset.url);
    if (pooledImage && asset.type === 'texture') {
      pooledImage.pipe(first()).subscribe((source) => {
        const texture = new Texture();
        texture.source = source;
        texture.needsUpdate = true;
        texture.minFilter = LinearMipMapLinearFilter;
        texture.magFilter = LinearFilter;
        asset.subject.next(texture);
        asset.subject.complete();
      });
      return;
    }
    //če nimamo sourca ga naloadamo
    const imageReplaySubject = new ReplaySubject<Source>(1);
    this._imagePool.set(asset.url, imageReplaySubject);

    const id = this.loadingService.startLoading();

    this.currentLoading.push(asset);
    this.currentLoading.sort((a, b) => a.priority - b.priority);
    const loader = asset.type === 'texture' ? this.imageLoader : this.gltfLoader;

    loader.loadAsync(asset.url).then(
      (loadedAsset) => {
        //če nalagamo sliko, tukaj poolamo source
        if (loadedAsset instanceof ImageBitmap) {
          const source = new Source(loadedAsset);
          imageReplaySubject.next(source);
        }
        if (asset.priority > 1) {
          let sub = this.sharedObservable.subscribe((res) => {
            if (res === loadedAsset) {
              if (res instanceof ImageBitmap) {
                const texture = new Texture(res);
                texture.needsUpdate;

                asset.subject.next(texture);
                asset.subject.complete();
              } else {
                asset.subject.next(res);
                asset.subject.complete();
              }
              sub.unsubscribe();
            } else {
            }
          });
          this.timerSubject.next(loadedAsset);
        } else {
          //če nalagamo teksturo, vemo da za trenutni request moramo vrnit novo teksturo
          if (loadedAsset instanceof ImageBitmap) {
            imageReplaySubject.pipe(first()).subscribe((source) => {
              const texture = new Texture();
              texture.source = source;
              texture.needsUpdate = true;
              texture.minFilter = LinearMipMapLinearFilter;
              texture.magFilter = LinearFilter;
              asset.subject.next(texture);
              asset.subject.complete();
            });
          } else {
            asset.subject.next(loadedAsset);
            asset.subject.complete();
          }
        }

        //debugger;
        // remove the loaded asset from currentLoading array
        this.currentLoading = this.currentLoading.filter((a) => a !== asset);
        this.loadingService.stopLoading(id);
        this.loadNextAssets();
      },
      (error) => {
        // eslint-disable-next-line
        console.error('There was an error loading the asset:', error);

        // remove the problematic asset from currentLoading array
        this.currentLoading = this.currentLoading.filter((a) => a !== asset);
        this.loadingService.stopLoading(id);
        this.loadNextAssets();
      },
    );
  }

  public resetPool() {
    this._texturePool = new Map<string, Observable<Texture | GLTF>>();
    this._imagePool = new Map<string, Observable<Source>>();
  }

  private getLowestPrirority(): number {
    let lowest = -1;
    if (this.currentLoading.length > 0) {
      lowest = this.currentLoading[0].priority;
    } else {
      lowest = this.loadingQueue[0].priority;
    }
    if (this.loadingQueue[0].priority < lowest) {
      lowest = this.loadingQueue[0].priority;
    }
    return lowest;
  }
}
